import { useLocation } from '@gatsbyjs/reach-router';
import React, { useEffect, useRef } from 'react';
import {
  addSlashIfMissing,
  isHomeUrl,
  runNavigationEventData,
} from '../../helpers/navigationHelpers';
import uniqueId from '../../helpers/uniqueId';
import { addLinkTestId } from '../../helpers/addTestId';
import { Link, withPrefix } from 'gatsby';
import { Link as ContentfulLink } from './types/Navigation.types';

type NavigationItemProps = {
  allLinks: ContentfulLink[];
  link: ContentfulLink;
  pageTitle: string;
  secondLanguageUrlPrefix: string | undefined;
  isSecondLanguageOnUrl: boolean | undefined;
};

const NavigationItem: React.FC<NavigationItemProps> = ({
  allLinks,
  link,
  pageTitle,
  secondLanguageUrlPrefix,
  isSecondLanguageOnUrl,
}) => {
  const location = useLocation();

  const navigationItemRef = useRef<HTMLDivElement>(null);

  // expends "active" state calculation to include query params
  const calculateActive = () => {
    if (!location.search) {
      return;
    }

    if (link.url.includes(location.search)) {
      return 'active';
    }

    const basePathname = allLinks.find(l => l.url === location.pathname)?.url;

    if (link.url === basePathname) {
      return 'not-active';
    }
  };

  // to override "active" state at client runtime
  useEffect(() => {
    navigationItemRef.current?.querySelectorAll('a').forEach(anchor => {
      const active = calculateActive();

      if (active) {
        anchor.classList.add(active);
      }
    });
  }, []);

  return (
    <div ref={navigationItemRef}>
      <Link
        to={addSlashIfMissing(link.url, {
          missingFrom: 'start',
        })}
        id={link.contentful_id}
        title={link.name}
        data-analytics-element={uniqueId(link.name)}
        data-analytics-context={pageTitle}
        data-analytics-category="Link"
        data-testid={addLinkTestId('navLink', link.name)}
        onClick={async () => {
          await runNavigationEventData({ location: 'header' });
        }}
        className={calculateActive()}
        activeClassName={'active'}
        partiallyActive={
          !isHomeUrl(
            withPrefix(link.url),
            secondLanguageUrlPrefix,
            isSecondLanguageOnUrl
          )
        }
      >
        {link.name}
      </Link>
    </div>
  );
};

export default NavigationItem;
